import {
  Column,
  CloseIcon,
  DateCellComponent,
  DefaultTableFilterProps,
  DetailsIcon,
  FlexContainer,
  Loader,
  ModernTableColumn,
  ModernTableFilterCatalogItem,
  PrintIcon,
  SuccessLineIcon,
  useNotifications,
} from '@uni-space/uni-components';

import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { BagDto, ManifestBagType, ParcelItemsDto, WeightType } from './manualSorting';
import { getActualWeightBag, getParcelByBagId, printLabel } from '../api';
import { getErrorMessageAsync } from '@shared/api';

export interface SliderStateBags {
  bags: ParcelItemsDto;
  bagCode: string;
  scales: WeightType;
  bagNumber: string;
  closedAt: Date | null;
  bagId: number;
}

const initSliderState = {
  bags: { items: [] },
  scales: { weight: 0, weightValue: '', parcelsCount: 0 },
  bagNumber: '',
  closedAt: null,
  bagCode: '',
  bagId: 0,
};

const BAG_TYPE_LABEL_KEYS: Record<ManifestBagType, string> = {
  [ManifestBagType.BoxM]: 'bagType.boxM',
  [ManifestBagType.Bag]: 'bagType.bag',
  [ManifestBagType.BoxL]: 'bagType.boxL',
  [ManifestBagType.BigBag]: 'bagType.bigBag',
  [ManifestBagType.LowDensityBag]: 'bagType.lowDensityBag',
  [ManifestBagType.BagDG]: 'bagType.bagDG',
  [ManifestBagType.BigBagDG]: 'bagType.bigBagDG',
};

export const useBagsSettingsTable = (props: { openSlider: () => void; isSortingTapeBags: boolean }) => {
  const { openSlider, isSortingTapeBags = false } = props;
  const [loadingItem, setLoadingItems] = useState<number[]>([]);
  const { addNotification } = useNotifications();
  const [loadingDetails, setLoadingDetails] = useState<number[]>([]);
  const [sliderState, setSliderState] = useState<SliderStateBags>(initSliderState);
  const { t } = useTranslation();

  const filters: DefaultTableFilterProps<BagDto>[] = useMemo(
    () => [{
      type: 'search',
      id: 'trackNumbers',
      name: 'Track Number',
      label: t('tableHeader.trackNumber'),
      variant: 'primary',
    },
    {
      type: 'date',
      id: 'createdAt',
      placeholder: t('tableHeader.dateCreate'),
    },
    {
      type: 'date',
      id: 'closedAt',
      placeholder: t('tableHeader.closedAt'),
    },
    ...(isSortingTapeBags
      ? ([
        {
          type: 'search',
          id: 'chuteNumbers',
          name: 'Chute Number',
          label: t('tableHeader.chuteNumber'),
          variant: 'primary',
        },
      ] as DefaultTableFilterProps<BagDto>[])
      : []),
    ],
    [t, isSortingTapeBags],
  );

  const handleResetState = () => {
    setSliderState(initSliderState);
  };

  const handleOpenDetails = async (bagId: number, bagNumber: string, closedAt: Date | null, bagCode: string) => {
    try {
      setLoadingDetails((prev) => [...prev, bagId]);
      const dataBag = await getParcelByBagId(bagId);
      const scales = await getActualWeightBag(bagId);
      setSliderState({ bags: dataBag.data, scales: scales, bagNumber, closedAt, bagId, bagCode });
      openSlider();
    } catch (error) {
      addNotification({ type: 'error', message: t('someInfoUnderloaded') });
    } finally {
      setLoadingDetails((prev) => prev.filter((i) => i !== bagId));
    }
  };

  const handleClickLabel = async (bagId: number) => {
    try {
      setLoadingItems((prev) => [...prev, bagId]);
      await printLabel(bagId);
    } catch (error: unknown) {
      addNotification({ type: 'error', message: await getErrorMessageAsync(error, t('unexpectedError')) });
    } finally {
      setLoadingItems((prev) => prev.filter((item) => item !== bagId));
    }
  };

  const columns: Column<BagDto>[] = useMemo(
    () => [
      {
        header: t('tableHeader.info'),
        accessor: 'number',
        textAlign: 'center',
        flex: 0.5,

        renderCell: (row) =>
          loadingDetails.includes(row.id) ? (
            <FlexContainer align="center" justify="center">
              <Loader size="s" />
            </FlexContainer>
          ) : (
            <DetailsIcon
              cursor="pointer"
              onClick={() => handleOpenDetails(row.id, row.number!, row.closedAt, row.bagCode)}
              size="s"
            />
          ),
      },
      {
        header: t('tableHeader.labelHeader'),
        accessor: 'number',
        textAlign: 'center',
        flex: 0.5,
        renderCell: (row) =>
          loadingItem.includes(row.id) ? (
            <FlexContainer align="center" justify="center">
              <Loader size="s" />
            </FlexContainer>
          ) : (
            <PrintIcon onClick={() => handleClickLabel(row.id)} size="s" />
          ),
      },
      {
        header: t('tableHeader.number'),
        accessor: 'number',
        textAlign: 'center',
      },
      {
        header: t('tableHeader.parcelsCount'),
        accessor: 'parcelsCount',
        textAlign: 'center',
        flex: 0.5,
      },
      {
        header: t('tableHeader.actualWeightHeader'),
        accessor: 'actualWeight',
        textAlign: 'center',
        renderCell: (row) => `${row.actualWeight?.value} g`,
      },
      {
        header: t('bagCode'),
        accessor: 'bagCode',
        textAlign: 'center',
      },
      ...(!isSortingTapeBags
        ? ([
          {
            header: t('tableHeader.bagType'),
            accessor: 'type',
            textAlign: 'center',
            flex: 0.7,
            renderCell: (row) =>
              row.type && BAG_TYPE_LABEL_KEYS[row.type] ? t(BAG_TYPE_LABEL_KEYS[row.type]) : '—',
          },
        ] as Column<BagDto>[])
        : []),
      {
        header: t('isPostal'),
        accessor: 'isPostal',
        textAlign: 'center',
        flex: 0.5,
        renderCell: (r) => (r.isPostal ? <SuccessLineIcon size="xs" color="green" /> : <CloseIcon color="red" size="xs" />),
      },
      {
        header: t('isReturn'),
        accessor: 'isReturn',
        textAlign: 'center',
        minWidth: '120px',
        flex: 0.5,
        renderCell: (row) => (row.isReturn ? t('returnFlag.yes') : t('returnFlag.no')),
      },
      {
        header: t('tableHeader.regionZone'),
        accessor: 'regionZone',
        textAlign: 'center',
        flex: 0.7,
      },
      {
        header: t('tableHeader.userName'),
        accessor: 'userName',
        textAlign: 'center',
        flex: 1.5,
      },
      {
        header: t('tableHeader.createdAt'),
        accessor: 'createdAt',
        textAlign: 'center',
        renderCell: (row) => <DateCellComponent justify="center" date={row.createdAt} zone="local" />,
      },
      {
        header: t('tableHeader.closedAt'),
        accessor: 'closedAt',
        textAlign: 'center',
        renderCell: (row) => <DateCellComponent justify="center" date={row.closedAt} zone="local" />,
      },

      ...(isSortingTapeBags
        ? ([
          {
            header: t('tableHeader.chuteNumber'),
            accessor: 'chuteNumber',
            textAlign: 'center',
          },
        ] as Column<BagDto>[])
        : []),
    ],
    [loadingItem, loadingDetails, t, isSortingTapeBags],
  );

  const modernColumns: ModernTableColumn<BagDto>[] = useMemo(
    () =>
      columns.map((column, index) => {
        // Колонки действий в старой таблице тоже используют accessor 'number'.
        const key = index === 0 ? 'details' : index === 1 ? 'label' : String(column.accessor);

        return {
          key,
          label: column.header,
          align: column.textAlign,
          sticky: key === 'number',
          width: index < 2 ? 90 : column.accessor === 'createdAt' || column.accessor === 'closedAt' ? 190 : 150,
          render: (row) =>
            column.renderCell
              ? column.renderCell(row)
              : String(column.accessor ? row[column.accessor] ?? '—' : '—'),
        };
      }),
    [columns],
  );

  const filterCatalog: ModernTableFilterCatalogItem<BagDto>[] = useMemo(
    () => [
      { key: 'trackNumbers', label: t('tableHeader.trackNumber'), type: 'text' },
      { key: 'createdAt', label: t('tableHeader.dateCreate'), type: 'daterange' },
      { key: 'closedAt', label: t('tableHeader.closedAt'), type: 'daterange' },
    ],
    [t],
  );

  return {
    filters,
    columns,
    modernColumns,
    filterCatalog,
    sliderState,
    handleResetState,
    handleOpenDetails,
    loadingDetails,
  };
};
